import {useRequiredConfig} from '@domain/shifthandover'
import {FilterDatePicker} from '@hconnect/common/components'
import {FlexPage} from '@hconnect/common/components/FlexPage'
import {useCancelInactiveQueries} from '@hconnect/common/hooks/useCancelInactiveQueries'
import {FilterContrastProvider} from '@hconnect/common/styles'
import {DateRange, SHIFT_QUICK_SELECT_SLOTS, ShiftQuickSelectSlots} from '@hconnect/common/types'
import {queryRangeToTimeRange} from '@hconnect/common/utils'
import {PageContainer} from '@hconnect/uikit'
import {useUpmPlantTimezone, useUpmPlantNow} from '@hconnect/upm'
import {Loading} from '@hm/fundamental'
import {Grid} from '@mui/material'
import {useQueryClient} from '@tanstack/react-query'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {LabQualityResultMeasurements, LabQualityResultNavigation} from '../containers/labData'
import {useFilterDatePicker, useLabQualityResultPageFilters, useLatestSampleData} from '../hooks'
import {useGetCqdbEquipments} from '../hooks/api/labData'

type Props = {
  plantId: string
  /**
   * Set to `true` when rendered on the legacy blue surface (Cockpit) so the
   * header controls use the on-blue contrast colours. Suite renders on the
   * light/dark page surface and keeps the default (`false`).
   */
  inverted?: boolean
}

const AVAILABLE_QUICK_SELECT_SLOTS: ShiftQuickSelectSlots[] = [
  SHIFT_QUICK_SELECT_SLOTS.CurrentShift,
  SHIFT_QUICK_SELECT_SLOTS.PastShift,
  SHIFT_QUICK_SELECT_SLOTS.Today,
  SHIFT_QUICK_SELECT_SLOTS.Last24hours,
  SHIFT_QUICK_SELECT_SLOTS.Yesterday
]

export const LabQualityResultPage: React.FC<Props> = ({plantId, inverted = false}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const queryClient = useQueryClient()
  useCancelInactiveQueries(queryClient)

  const {shifts} = useRequiredConfig(plantId)
  const getPlantNow = useUpmPlantNow()
  const timezone = useUpmPlantTimezone()
  const [filterOptions, setFilters] = useLabQualityResultPageFilters()
  const {getDateRange} = useFilterDatePicker(shifts)

  const extendedFilterOptions = {...filterOptions}
  if (!extendedFilterOptions.timeRange) {
    extendedFilterOptions.timeRange = getDateRange()
  }
  const [selectedItem, setSelectedItem] = useState<string | undefined>(undefined)
  const [timeRange, setTimeRange] = useState<DateRange | ShiftQuickSelectSlots>('currentShift')

  const summaryTimeRange = queryRangeToTimeRange(
    extendedFilterOptions.timeRange,
    shifts,
    getPlantNow()
  )

  const {
    data: labData,
    isLoading: isLoadingEquipments,
    isFetching
  } = useGetCqdbEquipments(plantId, summaryTimeRange)

  const handleLatestSampleTimeRange = useCallback(
    (dateRange: DateRange) => {
      setTimeRange(dateRange)
      setFilters({...filterOptions, timeRange: dateRange})
    },
    [filterOptions, setFilters]
  )

  const {
    latestSample,
    isLoading: isLoadingLatestSample,
    orderedEquipments
  } = useLatestSampleData({
    plantId,
    equipments: labData,
    timezone,
    onTimeRangeChange: handleLatestSampleTimeRange
  })

  const selectedEquipment = useMemo(() => {
    if (!orderedEquipments || !selectedItem) return undefined
    return orderedEquipments.find((eq) => eq.limsSamplingPointCode === selectedItem)
  }, [orderedEquipments, selectedItem])

  useEffect(() => {
    if (!selectedItem || !orderedEquipments?.length) return

    const hasSelectedItem = orderedEquipments.some(
      (equipment) => equipment.limsSamplingPointCode === selectedItem
    )

    if (!hasSelectedItem) {
      setSelectedItem(undefined)
    }
  }, [orderedEquipments, selectedItem])

  const isLoading = isLoadingEquipments || isLoadingLatestSample

  if (isLoading) {
    return (
      <PageContainer>
        <FlexPage>
          <Grid container justifyContent="center" alignItems="center" flex={1}>
            <Loading variant="secondary" size="large" />
          </Grid>
        </FlexPage>
      </PageContainer>
    )
  }

  return (
    <FlexPage
      appName="Cockpit"
      title={t('labQualityResult.pageName')}
      headerActionContent={
        <FilterContrastProvider isContrastMode={inverted}>
          <Grid container alignItems="center">
            <FilterDatePicker
              key={typeof timeRange === 'string' ? 'quick-select' : 'date-range'}
              shifts={shifts}
              timezone={timezone}
              defaultTimeRange={'currentShift'}
              plantNow={getPlantNow()}
              onChange={(value) => {
                setTimeRange(value)
                setFilters({...filterOptions, timeRange: value})
              }}
              value={timeRange}
              quickSelectSlots={AVAILABLE_QUICK_SELECT_SLOTS}
              disableDateRangeSelect
              language={language}
              translate={t}
            />
          </Grid>
        </FilterContrastProvider>
      }
    >
      <Grid container spacing={2} flex={1}>
        <Grid item xs={12} sm={3}>
          <LabQualityResultNavigation
            labData={orderedEquipments}
            isLoading={isLoadingEquipments}
            isFetching={isFetching}
            selectedItem={selectedItem}
            onItemChange={setSelectedItem}
          />
        </Grid>
        <Grid item container xs={12} sm={9}>
          <LabQualityResultMeasurements
            plantId={plantId}
            timeRange={summaryTimeRange}
            equipmentName={selectedEquipment?.limsSamplingPointCode || ''}
            productTypes={selectedEquipment?.types || []}
            latestEquipmentType={latestSample?.type}
          />
        </Grid>
      </Grid>
    </FlexPage>
  )
}
